import { useState, useEffect, useMemo } from 'react';
import { Card, Spinner, Alert } from 'react-bootstrap';
import { ResponsiveLine } from '@nivo/line';
import api from '../../services/api';
import { formataDataExibicao } from '../../common/utils';

const PhenylalanineHistoryChart = ({ atendimentoId }) => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            if (!atendimentoId) return;

            setLoading(true);
            try {
                const response = await api.get(`/atendimentos/historico-fenilalanina/${atendimentoId}/`);
                setData(response.data);
                setError(null);
            } catch (err) {
                console.error("Erro ao buscar histórico de fenilalanina:", err);
                setError("Não foi possível carregar o histórico de exames.");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [atendimentoId]);

    const chartData = useMemo(() => {
        if (!data || data.length === 0) return [];

        const seriesData = data.map(item => ({
            x: formataDataExibicao(item.data),
            y: parseFloat(item.valor) || 0,
            originalData: item
        }));

        return [
            {
                id: "Níveis de Fenilalanina",
                data: seriesData
            }
        ];
    }, [data]);

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                <Spinner animation="border" variant="primary" />
            </div>
        );
    }

    if (error) {
        return <Alert variant="danger">{error}</Alert>;
    }

    if (!data || data.length === 0) {
        return null;
    }

    return (
        <Card className="mb-4 shadow-sm">
            <Card.Header className="bg-transparent border-0 pb-0">
                <h6 className="fw-bold mb-0 text-dark">Histórico de Níveis de Fenilalanina (Phe)</h6>
            </Card.Header>
            <Card.Body style={{ height: '400px' }}>
                <ResponsiveLine
                    data={chartData}
                    margin={{ top: 40, right: 40, bottom: 50, left: 40 }}
                    xScale={{ type: 'point' }}
                    yScale={{
                        type: 'linear',
                        min: 'auto',
                        max: 'auto',
                        stacked: false,
                        reverse: false
                    }}
                    yFormat={value => new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2 }).format(value)}
                    curve="monotoneX"
                    enableArea={true}
                    areaOpacity={0.15}
                    colors={['#0d6efd']}
                    axisTop={null}
                    axisRight={null}
                    enableGridX={false}
                    axisBottom={{
                        tickSize: 0,
                        tickPadding: 15,
                        tickRotation: 0,
                        legend: '',
                        legendOffset: 36,
                        legendPosition: 'middle'
                    }}
                    axisLeft={null}
                    pointSize={8}
                    pointColor={{ theme: 'background' }}
                    pointBorderWidth={2}
                    pointBorderColor={{ from: 'serieColor' }}
                    enablePointLabel={true}
                    pointLabel={d => new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2 }).format(d.data.y)}
                    pointLabelYOffset={-14}
                    useMesh={true}
                    enableSlices="x"
                    sliceTooltip={({ slice }) => {
                        return (
                            <div
                                style={{
                                    background: 'white',
                                    padding: '9px 12px',
                                    border: '1px solid #ccc',
                                    borderRadius: '4px',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                }}
                            >
                                {slice.points.map(point => (
                                    <div key={point.id} style={{ padding: '3px 0' }}>
                                        <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>{point.data.x}</div>
                                        <div style={{ color: point.serieColor }}>
                                            <strong>Nível:</strong> {new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2 }).format(point.data.y)}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        );
                    }}
                    legends={[]} 
                />
            </Card.Body>
        </Card>
    );
};

export default PhenylalanineHistoryChart;
